/* @flow */

import * as React from 'react'
import styled, { css } from 'styled-components'
import { Link } from 'react-router-dom'
import sortBy from 'lodash/sortBy'
import { FiHeart, FiSearch, FiShoppingCart } from 'react-icons/fi'

import CloudinaryResource from '../../../../infrastructure/components/CloudinaryResource'
import { unwrapTemporaryFile } from '../../../../infrastructure/components/Formik'
import { resolveSingleFont } from '../../../../infrastructure/components/CustomFont/helpers'
import {
  SessionContext,
  Tooltip,
  removeNonDigits,
  addPxToInput,
} from '../../../shared'
import { useWebshopNavigationLinks } from '../../hooks'
import { WebshopContext } from '../../shared'
import WebshopNavigation from '../NavigationV2/WebshopNavigation'
import WebshopMobileNavigation from '../NavigationV2/WebshopMobileNavigation'
import { WidgetContainer } from '../WebshopWidgets/shared'
import { useWidgetCustomFont } from '../WebshopWidgets/shared'

const WebshopHeaderModern = ({ brand, gridItem, outsideProps = {} }) => {
  const { entity } = React.useContext(SessionContext)
  const {
    favorites,
    isMobile,
    loadFont,
    query,
    shopUrl,
    totals,
    webshopSession,
    webshopSettings,
  } = React.useContext(WebshopContext)

  const { onSubmitSearch, isOnBackdrop, isShowingSplash } = outsideProps

  const [isSearchFocused, setIsSearchFocused] = React.useState(false)

  const [showMobileNavigation, setShowMobileNavigation] = React.useState(false)

  const logoPosition: 'center' | 'left' | 'mobile' = isMobile
    ? 'mobile'
    : gridItem.data.logo_position || 'center'

  const { brandLogo, logoTransformations, logoStyle } = React.useMemo(() => {
    let brandLogo = brand.logo
    const logoTransformations = {
      // so it works when a logo is transparent
      format: 'png',
    }
    const logoStyle = {}

    if (gridItem.data.logo_size_mode === 'set_height') {
      logoTransformations.height = removeNonDigits(gridItem.data.logo_height)
    }
    if (gridItem.data.logo_size_mode === 'set_width') {
      logoTransformations.width = removeNonDigits(gridItem.data.logo_width)
    }

    // unpack files in case they are just uploaded but not persisted yet
    if (isOnBackdrop && gridItem.data.backdrop_logo_override) {
      const backdropLogoOverride = unwrapTemporaryFile(
        gridItem.data.backdrop_logo_override
      )

      if (backdropLogoOverride) {
        brandLogo = backdropLogoOverride
      }
    }

    // On mobile we want the logo to shrink based on the available width, thats
    // why add the width: 100%;
    if (isMobile) {
      logoStyle.width = '100%'
    }

    return {
      brandLogo,
      logoTransformations,
      logoStyle,
    }
  }, [brand.logo, gridItem.data, isOnBackdrop, isMobile])

  const [navigationFont] = useWidgetCustomFont(
    loadFont,
    gridItem.data.navigation_font
  )

  const navigationFontColor = React.useMemo(() => {
    return isOnBackdrop === true
      ? gridItem.data.backdrop_navigation_font_color
      : gridItem.data.navigation_font_color
  }, [
    isOnBackdrop,
    gridItem.data.backdrop_navigation_font_color,
    gridItem.data.navigation_font_color,
  ])
  const navigationFontHoverColor = React.useMemo(() => {
    return isOnBackdrop === true
      ? gridItem.data.backdrop_navigation_font_hover_color
      : gridItem.data.navigation_font_hover_color
  }, [
    isOnBackdrop,
    gridItem.data.backdrop_navigation_font_hover_color,
    gridItem.data.navigation_font_hover_color,
  ])

  const navigationFetchArgs = React.useMemo(() => {
    return [brand.id, webshopSession.id]
  }, [brand, webshopSession])

  const [
    navigationLinks,
    isFetchingNavigationLinks,
    { isInitialized: isNavigationLinksInitialized },
  ] = useWebshopNavigationLinks(navigationFetchArgs)

  const sortedNavigationLinks = React.useMemo(() => {
    return sortBy(navigationLinks, 'sort_order')
  }, [navigationLinks])

  if (!isNavigationLinksInitialized) {
    return null
  }

  return (
    <HeaderContainer gridItem={gridItem}>
      <HeaderNavigationContainer logoPosition={logoPosition}>
        {!isShowingSplash && isMobile && (
          <WebshopMobileNavigation
            brand={brand}
            navigationFontColor={navigationFontColor}
            navigationLinks={sortedNavigationLinks}
            onHide={() => setShowMobileNavigation(false)}
            onShow={() => setShowMobileNavigation(true)}
            shopUrl={shopUrl}
            show={showMobileNavigation}
          />
        )}
        {!isShowingSplash && !isMobile && (
          <WebshopNavigation
            brand={brand}
            navigationLinks={sortedNavigationLinks}
            shopUrl={shopUrl}
            navigationFont={navigationFont}
            navigationFontColor={navigationFontColor}
            navigationFontHoverColor={navigationFontHoverColor}
            navigationFontSize={gridItem.data.navigation_font_size}
            navigationHoverUnderline={gridItem.data.navigation_hover_underline}
            navigationSpaceBetweenEach={
              gridItem.data.navigation_space_between_each
            }
            navigationUppercase={gridItem.data.navigation_uppercase}
          />
        )}
      </HeaderNavigationContainer>

      <HeaderLogoContainer
        logoPosition={logoPosition}
        spaceBetweenLogoAndNavigation={
          gridItem.data.space_between_logo_and_navigation || 30
        }
      >
        <Link to={shopUrl}>
          <CloudinaryResource
            brandId={brand.id}
            id={brandLogo}
            presets="logo_webshop"
            fallback="logo_black"
            transformationOverrides={logoTransformations}
            style={logoStyle}
          />
        </Link>
      </HeaderLogoContainer>

      <HeaderRightSideContainer logoPosition={logoPosition}>
        {!isShowingSplash && (
          <>
            <SearchButtonContainer>
              <SearchButton
                type="button"
                navigationFontColor={navigationFontColor}
                navigationFontHoverColor={navigationFontHoverColor}
                onClick={() => setIsSearchFocused(true)}
              >
                <FiSearch size={24} />
              </SearchButton>
              <SearchBox
                isSearchFocused={isSearchFocused}
                setIsSearchFocused={setIsSearchFocused}
                searchQuery={query.q || ''}
                onSubmitSearch={onSubmitSearch}
              />
            </SearchButtonContainer>
            <HeaderIconLink
              to={`${shopUrl}/favorites`}
              navigationFontColor={navigationFontColor}
              navigationFontHoverColor={navigationFontHoverColor}
              withBadge={favorites.total > 0}
            >
              <FiHeart size={24} />
              {favorites.total > 0 && (
                <HeaderIconButtonBadge>{favorites.total}</HeaderIconButtonBadge>
              )}
            </HeaderIconLink>
            <HeaderIconLink
              to={`${shopUrl}/cart`}
              navigationFontColor={navigationFontColor}
              navigationFontHoverColor={navigationFontHoverColor}
              withBadge
            >
              <FiShoppingCart size={24} />
              <HeaderIconButtonBadge>{totals.quantity}</HeaderIconButtonBadge>
            </HeaderIconLink>
          </>
        )}
      </HeaderRightSideContainer>
    </HeaderContainer>
  )
}

export default WebshopHeaderModern

const HeaderContainer = styled(WidgetContainer).attrs({
  // Need class for custom CSS hook
  className: 'webshop-header',
})`
  align-items: center;
  display: flex;
`

const HeaderNavigationContainer = styled.div`
  flex: 1 0 0px;

  ${({ logoPosition }) =>
    logoPosition === 'left' &&
    css`
      order: 2;
    `}
`

const HeaderLogoContainer = styled.div`
  flex: 0 0 auto;

  ${props =>
    props.logoPosition === 'center' &&
    css`
      margin: 0 ${addPxToInput(props.spaceBetweenLogoAndNavigation)};
    `}

  ${props =>
    props.logoPosition === 'left' &&
    css`
      margin-right: ${addPxToInput(props.spaceBetweenLogoAndNavigation)};
      order: 1;
    `}

  ${props =>
    props.logoPosition === 'mobile' &&
    css`
      margin: 0 ${addPxToInput(props.spaceBetweenLogoAndNavigation)};
      flex-shrink: 1;
    `}
`

const HeaderRightSideContainer = styled.div`
  margin-left: 15px;
  display: flex;
  justify-content: flex-end;
  align-items: center;

  ${({ logoPosition }) =>
    logoPosition === 'center' &&
    css`
      flex: 1 0 0px;
    `}

  ${({ logoPosition }) =>
    logoPosition === 'left' &&
    css`
      flex: 0 0 auto;
      order: 3;
    `}
`

const HeaderIconLink = styled(Link)`
  display: block;
  margin-left: 15px;
  position: relative;
  color: ${props => props.navigationFontColor || '#000000'};

  ${({ withBadge }) =>
    withBadge &&
    css`
      margin-right: 5px;
    `}

  & > svg {
    display: block;
  }

  &:hover,
  &:focus-visible {
    color: ${props => props.navigationFontHoverColor || '#000000'};
  }
`

const HeaderIconButtonBadge = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(50%, -25%);
  background: white;
  color: black;
  border-radius: 9px;
  box-shadow: 0px 0px 2px 0px rgba(0, 0, 0, 0.5);
  padding: 4px 4px 2px 4px;
  font-size: 10px;
  line-height: 1;
`

const SearchButtonContainer = styled.div`
  position: relative;
`

const SearchButton = styled.button`
  display: block;
  position: relative;
  padding: 0;
  background: none;
  border: none;
  color: ${props => props.navigationFontColor || '#000000'};

  & > svg {
    display: block;
  }

  &:hover,
  &:focus-visible {
    color: ${props => props.navigationFontHoverColor || '#000000'};
  }
`

const SearchBox = ({
  isSearchFocused,
  setIsSearchFocused,
  searchQuery,
  onSubmitSearch,
}: {
  isSearchFocused: boolean,
  setIsSearchFocused: (isSearchFocused: boolean) => void,
  searchQuery: string,
  onSubmitSearch: (query: string) => void,
}) => {
  const inputRef = React.useRef()
  const [value, setValue] = React.useState(searchQuery)

  React.useEffect(() => {
    if (isSearchFocused && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isSearchFocused])

  const onSubmit = e => {
    e.preventDefault()

    if (value === '') {
      setIsSearchFocused(true)
    } else {
      onSubmitSearch(value)
    }
  }

  return (
    <SearchBoxForm show={isSearchFocused || value !== ''} onSubmit={onSubmit}>
      <SearchBoxInput
        ref={inputRef}
        name="query"
        type="text"
        placeholder="Search…"
        onFocus={() => setIsSearchFocused(true)}
        onBlur={() => setIsSearchFocused(false)}
        value={value}
        onChange={e => setValue(e.target.value)}
      />
      <SearchBoxSubmitButton type="submit">
        <FiSearch size={24} />
      </SearchBoxSubmitButton>
    </SearchBoxForm>
  )
}

const SearchBoxForm = styled.form`
  position: absolute;
  top: -7px;
  right: -7px;
  width: 230px;
  height: 40px;
  background: white;
  border: 1px solid #cccccc;
  display: ${props => (props.show ? 'flex' : 'none')};
  align-items: center;
`

const SearchBoxInput = styled.input`
  width: 100%;
  margin: 0;
  padding: 0;
  border: none !important;
  background: transparent !important;
`

const SearchBoxSubmitButton = styled.button`
  border: none;
  background: transparent;
  padding: 8px 6px 4px 6px;
  margin: 0;
`
